import hljs from "highlight.js/lib/common"

const HIGHLIGHTED_ATTRIBUTE = "data-highlighted"

// hljs/lib/common registers ~37 common languages. Anything outside that set
// (or an auto-detected name the editor stored, e.g. "vbscript-html") falls
// back to highlightAuto so the portal/widget match the dashboard editor, which
// highlights via lowlight's highlightAuto for unregistered languages.
const highlightElement = (code: HTMLElement): void => {
	const language = code.parentElement?.getAttribute("data-language")
	const text = code.textContent || ""
	if (!text.trim()) return
	try {
		const result =
			language && language !== "plaintext" && hljs.getLanguage(language)
				? hljs.highlight(text, { language })
				: hljs.highlightAuto(text)
		code.innerHTML = result.value
	} catch {
		// leave the block unhighlighted on any parser error
	}
}

export const highlightCodeBlocks = (html: string): string => {
	if (typeof DOMParser === "undefined") return html
	try {
		const doc = new DOMParser().parseFromString(html, "text/html")
		doc
			.querySelectorAll("pre code")
			.forEach((block) => highlightElement(block as HTMLElement))
		return doc.body.innerHTML
	} catch {
		return html
	}
}

// For a body already in the document: the review diff renders article HTML
// directly, so it has no string to run through `highlightCodeBlocks`. A block
// carrying word-diff marks is left alone, since highlighting would drop them.
export const highlightCodeBlocksIn = (root: ParentNode): void => {
	root.querySelectorAll<HTMLElement>("pre code").forEach((code) => {
		if (code.getAttribute(HIGHLIGHTED_ATTRIBUTE) === "true") return
		if (code.querySelector("[data-diff-word]")) return
		code.setAttribute(HIGHLIGHTED_ATTRIBUTE, "true")
		highlightElement(code)
	})
}

// Same skip rules as `highlightCodeBlocksIn`, for a body that React renders from
// a string. Highlighting in the markup means it does not wait on an effect.
export const highlightCodeBlocksHtml = (html: string): string => {
	if (typeof DOMParser === "undefined") return html
	if (!html.includes("<pre")) return html
	try {
		const doc = new DOMParser().parseFromString(html, "text/html")
		highlightCodeBlocksIn(doc.body)
		return doc.body.innerHTML
	} catch {
		return html
	}
}
